import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ExternalLink } from "lucide-react";

import { SurchargeCode } from "@/components/common/SurchargeCode";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SensitiveContractRef, SensitiveMoney } from "@/components/common/Sensitive";
import { normalizeContractRef } from "@/lib/formatContract";
import { cn } from "@/lib/utils";
import type {
  KaiRateComparison,
  KaiRateFamily,
  KaiRateRow,
  KaiRateSurchargeLine,
} from "./kaiTypes";
import { buildFamilyRatesUrl, buildRateCardUrl } from "./kaiRateLinks";

/** Exact USD string for money quotes (no K/M abbreviation). */
function usd(value: number | null | undefined): string {
  if (value == null) return "—";
  return `$${Math.round(value).toLocaleString()}`;
}

function kg(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${Math.round(value).toLocaleString()} kg`;
}

function formatEquipmentLabel(
  size?: string | null,
  type?: string | null,
): string | null {
  const s = (size ?? "").trim();
  if (!s) return null;
  const t = (type ?? "").trim();
  const showType = t && t.toUpperCase() !== "DV";
  return showType ? `${s}' ${t}` : `${s}'`;
}

function basisSuffix(basis?: string | null): string {
  const b = (basis ?? "").trim().toLowerCase();
  if (!b || b === "container") return "";
  if (b === "bl") return "/BL";
  if (b === "teu") return "/TEU";
  if (b === "cbm") return "/CBM";
  if (b === "ton") return "/ton";
  if (b === "shipment") return "/shipment";
  return `/${b}`;
}

function surchargeChipAmount(s: KaiRateSurchargeLine): string {
  return `${usd(s.amount_usd)}${basisSuffix(s.basis)}`;
}

const SURCHARGE_GROUPS: {
  key: string;
  label: string;
  match: (c?: string) => boolean;
}[] = [
  { key: "ocean", label: "Ocean surcharges", match: (c) => c === "ocean" },
  { key: "local", label: "Local surcharges", match: (c) => c === "local" },
  {
    key: "other",
    label: "Other surcharges",
    match: (c) => c !== "ocean" && c !== "local" && c !== "inclusive",
  },
  { key: "inclusive", label: "Inclusive (in freight)", match: (c) => c === "inclusive" },
];

/** A baseline -> adjusted price pair when an overlay moved the number. */
function priceWithBaseline(
  current: number | null | undefined,
  baseline: number | null | undefined,
): ReactNode {
  if (baseline != null && current != null && baseline !== current) {
    return (
      <>
        <SensitiveMoney formatted={usd(baseline)} /> →{" "}
        <SensitiveMoney formatted={usd(current)} />
      </>
    );
  }
  return <SensitiveMoney formatted={usd(current)} />;
}

const SOURCE_TYPES = new Set(["prepaid", "collect", "spot"]);
const MODES = new Set(["comparison", "lookup", "best_price"]);

/**
 * Parse and validate a `rate_comparison` fenced-block body. Returns `null` on
 * any shape mismatch so the caller falls back to a placeholder/error. Unknown
 * optional fields pass through untouched.
 */
export function parseRateComparisonSpec(json: string): KaiRateComparison | null {
  try {
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.mode !== undefined && !MODES.has(parsed.mode)) return null;
    if (!Array.isArray(parsed.families) || parsed.families.length === 0) {
      return null;
    }
    for (const family of parsed.families) {
      if (!family || typeof family.id !== "string" || !family.id) return null;
      if (typeof family.label !== "string") return null;
      if (!SOURCE_TYPES.has(family.source_type)) return null;
      if (!Array.isArray(family.rows)) return null;
      for (const row of family.rows) {
        if (typeof row.frt_usd !== "number") return null;
        if (typeof row.all_in_usd !== "number") return null;
      }
    }
    return parsed as KaiRateComparison;
  } catch {
    return null;
  }
}

function familyBadgeVariant(
  sourceType: string,
): "default" | "urgent" | "info" | "secondary" {
  if (sourceType === "prepaid") return "urgent";
  if (sourceType === "collect") return "info";
  return "secondary";
}

function validityRange(row: KaiRateRow): string {
  if (!row.valid_from && !row.valid_to) return "—";
  return `${row.valid_from ?? "…"} → ${row.valid_to ?? "…"}`;
}

function ValidityIndicator({ row }: { row: KaiRateRow }) {
  switch (row.validity_status) {
    case "active_today":
      return <span className="text-xs text-emerald-600 dark:text-emerald-400">Active</span>;
    case "starting_soon":
      return (
        <span className="text-xs text-sky-600 dark:text-sky-400">
          {row.valid_from ? `Starts ${row.valid_from}` : "Starting soon"}
        </span>
      );
    case "future":
      return (
        <span className="text-xs text-muted-foreground">
          {row.valid_from ? `Future (${row.valid_from})` : "Future"}
        </span>
      );
    case "expired":
      return <span className="text-xs text-destructive">Expired</span>;
    default:
      return (
        <span className="text-xs text-muted-foreground">{validityRange(row)}</span>
      );
  }
}

function NoticeChips({ row }: { row: KaiRateRow }) {
  if (!row.notices || row.notices.length === 0) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {row.notices.map((n, i) => (
        <Badge key={i} variant="urgent" className="text-[10px]">
          {n.title}
          {n.delta_usd != null ? ` ${usd(n.delta_usd)}` : ""}
        </Badge>
      ))}
    </span>
  );
}

function MetaRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-sm text-foreground">{value}</span>
    </div>
  );
}

/** Full awareness detail for a single rate row (shown on expand). */
function RowAwarenessDetail({ row }: { row: KaiRateRow }) {
  const navigate = useNavigate();
  const wb = row.weight_basis;

  const items: { label: string; value: ReactNode }[] = [
    { label: "Freight", value: priceWithBaseline(row.frt_usd, row.baseline_frt_usd) },
  ];

  if (row.frt_pp_usd != null && Number.isFinite(row.frt_pp_usd)) {
    items.push({
      label: "FRT++",
      value: <strong>{priceWithBaseline(row.frt_pp_usd, row.baseline_frt_pp_usd)}</strong>,
    });
  }

  items.push(
    { label: "Surcharges", value: <SensitiveMoney formatted={usd(row.surcharges_usd)} /> },
    {
      label: "All-in",
      value: <strong>{priceWithBaseline(row.all_in_usd, row.baseline_all_in_usd)}</strong>,
    },
    { label: "Validity", value: validityRange(row) },
  );

  if (wb) {
    items.push({ label: "Cargo weight", value: kg(wb.cargo_kg) });
    if (wb.gross_kg != null) {
      items.push({
        label: "Gross weight",
        value: `${kg(wb.gross_kg)}${
          wb.weight_basis_label ? ` (${wb.weight_basis_label})` : ""
        }`,
      });
    }
    if (wb.ows_tier || wb.ows_usd != null) {
      items.push({
        label: "Overweight",
        value: `${wb.ows_tier ?? ""} ${usd(wb.ows_usd)}`.trim(),
      });
    }
  }

  if (row.policy_hints?.booking) {
    items.push({ label: "Booking policy", value: row.policy_hints.booking });
  }
  if (row.policy_hints?.dnd) {
    items.push({ label: "D&D policy", value: row.policy_hints.dnd });
  }

  const cardUrl = buildRateCardUrl(row.rate_card_id);

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
        {items.map((item, i) => (
          <MetaRow key={i} label={item.label} value={item.value} />
        ))}
      </div>

      {row.surcharges && row.surcharges.length > 0 ? (
        <div className="flex flex-col gap-2">
          {SURCHARGE_GROUPS.map((group) => {
            const groupItems = row.surcharges!.filter((s) => group.match(s.category));
            if (groupItems.length === 0) return null;
            return (
              <div key={group.key} className="flex flex-col gap-1">
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  {group.label}
                </span>
                <div className="flex flex-wrap gap-1">
                  {groupItems.map((s, i) => (
                    <Badge
                      key={`${s.code}-${i}`}
                      variant={group.key === "inclusive" ? "secondary" : "outline"}
                      className="text-[10px]"
                    >
                      <SurchargeCode code={s.code} /> {surchargeChipAmount(s)}
                    </Badge>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        row.surcharge_codes &&
        row.surcharge_codes.length > 0 && (
          <div className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Surcharges
            </span>
            <div className="flex flex-wrap gap-1">
              {row.surcharge_codes.map((code, i) => (
                <Badge key={`${code}-${i}`} variant="outline" className="text-[10px]">
                  <SurchargeCode code={code} />
                </Badge>
              ))}
            </div>
            <span className="text-[11px] text-muted-foreground">
              Per-code amounts unavailable.
            </span>
          </div>
        )
      )}

      {cardUrl && (
        <Button
          variant="link"
          size="sm"
          className="h-auto justify-start px-0"
          onClick={() => navigate(cardUrl)}
        >
          <ExternalLink className="size-3.5" />
          View rate card
        </Button>
      )}
    </div>
  );
}

/** Collapsed row headline — stacked lines so long notice badges do not crush the title. */
function RateRowSummary({ row }: { row: KaiRateRow }) {
  const who = row.carrier ?? row.contract_ref ?? "Rate";
  const equip = formatEquipmentLabel(row.equipment_size, row.equipment_type);
  const contractToken = normalizeContractRef(row.contract_ref, row.carrier);
  const showContract =
    contractToken && contractToken !== who ? contractToken : null;

  return (
    <>
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
        <span className="text-sm font-medium text-foreground">{who}</span>
        {equip && (
          <>
            <span className="text-muted-foreground/60" aria-hidden>
              ·
            </span>
            <span className="text-sm text-foreground">{equip}</span>
          </>
        )}
        {showContract && (
          <>
            <span className="text-muted-foreground/60" aria-hidden>
              ·
            </span>
            <span className="truncate text-sm text-muted-foreground">
              <SensitiveContractRef value={row.contract_ref} carrier={row.carrier} />
            </span>
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm tabular-nums">
        {row.frt_pp_usd != null && Number.isFinite(row.frt_pp_usd) && (
          <>
            <span className="font-medium text-foreground">
              <SensitiveMoney formatted={usd(row.frt_pp_usd)} />{" "}
              <span className="text-xs font-normal text-muted-foreground">FRT++</span>
            </span>
            <span className="text-muted-foreground/50" aria-hidden>
              ·
            </span>
          </>
        )}
        <span className="font-medium text-foreground">
          <SensitiveMoney formatted={usd(row.all_in_usd)} />{" "}
          <span className="text-xs font-normal text-muted-foreground">all-in</span>
        </span>
        <ValidityIndicator row={row} />
      </div>
    </>
  );
}

function RateRowSection({
  row,
  index,
}: {
  row: KaiRateRow;
  index: number;
}) {
  const [open, setOpen] = useState(index === 0);
  const hasNotices = (row.notices?.length ?? 0) > 0;

  return (
    <div className="min-w-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full gap-2 px-3 py-2.5 text-left transition-colors hover:bg-foreground/[0.03]"
      >
        <ChevronDown
          className={cn(
            "mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <RateRowSummary row={row} />
          {hasNotices && (
            <div className="pt-0.5">
              <NoticeChips row={row} />
            </div>
          )}
        </div>
      </button>
      {open && (
        <div className="border-t border-border/40 bg-foreground/[0.015] px-3 pb-3 pt-2">
          <RowAwarenessDetail row={row} />
        </div>
      )}
    </div>
  );
}

function FamilyHeader({
  family,
  onOpenRates,
}: {
  family: KaiRateFamily;
  onOpenRates: (() => void) | null;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        <Badge variant={familyBadgeVariant(family.source_type)} className="capitalize">
          {family.source_type}
        </Badge>
        <span className="truncate text-sm font-medium text-foreground">
          {family.label}
        </span>
      </div>
      {onOpenRates && (
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 gap-1.5"
          onClick={onOpenRates}
        >
          <ExternalLink className="size-3.5" />
          Open in Rates
        </Button>
      )}
    </div>
  );
}

function RateFamilySection({ family }: { family: KaiRateFamily }) {
  const navigate = useNavigate();
  const ratesUrl = buildFamilyRatesUrl(family);
  const onOpenRates = ratesUrl ? () => navigate(ratesUrl) : null;

  return (
    <section className="flex min-w-0 flex-col gap-2">
      <FamilyHeader family={family} onOpenRates={onOpenRates} />
      {family.rows.length === 0 ? (
        <p className="py-1 text-center text-xs text-muted-foreground">
          No matching rate lines
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border/50 bg-foreground/[0.02]">
          <div className="divide-y divide-border/40">
            {family.rows.map((row, i) => (
              <RateRowSection
                key={`${row.rate_line_id ?? row.contract_ref ?? "row"}-${i}`}
                row={row}
                index={i}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function CompactQuoteCard({
  spec,
  family,
  row,
}: {
  spec: KaiRateComparison;
  family: KaiRateFamily;
  row: KaiRateRow;
}) {
  const navigate = useNavigate();
  const ratesUrl = buildFamilyRatesUrl(family);
  const equip = formatEquipmentLabel(row.equipment_size, row.equipment_type);

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {spec.title && (
            <p className="text-xs text-muted-foreground">{spec.title}</p>
          )}
          <p className="text-xl font-semibold text-foreground">
            <SensitiveMoney formatted={usd(row.all_in_usd)} /> all-in
          </p>
        </div>
        {ratesUrl && (
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 gap-1.5"
            onClick={() => navigate(ratesUrl)}
          >
            <ExternalLink className="size-3.5" />
            Open in Rates
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={familyBadgeVariant(family.source_type)} className="capitalize">
          {family.source_type}
        </Badge>
        {equip && (
          <Badge variant="outline" className="text-[10px]">
            {equip}
          </Badge>
        )}
        <span className="text-xs text-muted-foreground">{family.label}</span>
        {row.contract_ref && (
          <span className="text-xs text-muted-foreground">
            <SensitiveContractRef
              value={row.contract_ref}
              carrier={row.carrier}
            />
          </span>
        )}
        <ValidityIndicator row={row} />
        <NoticeChips row={row} />
      </div>

      {row.frt_pp_usd != null && Number.isFinite(row.frt_pp_usd) && (
        <p className="text-sm">
          <strong>
            {priceWithBaseline(row.frt_pp_usd, row.baseline_frt_pp_usd)} FRT++
          </strong>{" "}
          <span className="text-xs text-muted-foreground">
            (excludes B/L fees and terminal handling THC/THD)
          </span>
        </p>
      )}

      <div className="border-t border-border/40 pt-3">
        <RowAwarenessDetail row={row} />
      </div>
    </div>
  );
}

/** Root layout for rate blocks — lives inside the Kai message bubble (no nested glass panel). */
const RATE_BLOCK_ROOT = "my-3 flex min-w-0 w-full flex-col gap-4";

function Footer({ spec }: { spec: KaiRateComparison }) {
  if (
    (!spec.footnotes || spec.footnotes.length === 0) &&
    !spec.disclaimer
  ) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1">
      {spec.footnotes && spec.footnotes.length > 0 && (
        <ul className="list-disc pl-4 text-xs text-muted-foreground">
          {spec.footnotes.map((f, i) => (
            <li key={i}>{f}</li>
          ))}
        </ul>
      )}
      {spec.disclaimer && (
        <p className="text-xs text-muted-foreground/80">{spec.disclaimer}</p>
      )}
    </div>
  );
}

/**
 * Renders a `rate_comparison` artifact — a single-lane lookup, a best-price
 * sweep, or a family comparison. Each row expands to reveal full awareness
 * detail; families deep-link into the filtered Rates view.
 */
export function KaiRateComparison({ spec }: { spec: KaiRateComparison }) {
  const populatedFamilies = spec.families.filter((f) => f.rows.length > 0);
  const totalRows = populatedFamilies.reduce((n, f) => n + f.rows.length, 0);

  const isCompact =
    spec.mode === "lookup" && populatedFamilies.length === 1 && totalRows === 1;

  if (isCompact) {
    const family = populatedFamilies[0];
    return (
      <div className={RATE_BLOCK_ROOT}>
        <CompactQuoteCard spec={spec} family={family} row={family.rows[0]} />
        <Footer spec={spec} />
      </div>
    );
  }

  return (
    <div className={RATE_BLOCK_ROOT}>
      {spec.title && (
        <p className="text-sm font-semibold leading-snug text-foreground">{spec.title}</p>
      )}
      {spec.families.map((family) => (
        <RateFamilySection key={family.id} family={family} />
      ))}
      <Footer spec={spec} />
    </div>
  );
}
